import { consolidateBuiltinComponents } from "./builtin-component-sharing.mjs";
import { normalizeNavigationTabStyle } from "./navigation-tab-style.mjs";
import {
  Children,
  Fragment,
  createContext,
  cloneElement,
  isValidElement,
  useContext,
  useEffect,
  useState,
  useRef,
  type CSSProperties,
  type ReactNode,
} from "react";
import type { VideoItem } from "./data";
import schema from "./template-pages-schema.json";
import {
  getAppTemplate,
  getComponentVideos,
  getNavigationGroup,
  type NavigationItem,
  getComponentAds,
  reportComponentAd,
  adaptVideo,
  mediaUrl,
  IS_DEMO,
  type RemoteVideo,
  type ResolvedAds,
  type ResolvedAdItem,
  type CustomNavigation,
} from "./api";
export type { TemplatePageConfig } from "./template-pages.mjs";
import {
  normalizeTemplatePages,
  normalizeTemplateTheme,
  type TemplatePageConfig,
} from "./template-pages.mjs";
import {
  normalizeComponents,
  normalizeBottomMenus,
  type AppComponent,
  type BottomMenu,
} from "./template-platform.mjs";
import {
  normalizeRenderers,
  gridSlots,
  scopedRendererCss,
  rendererScope,
  boundVideoFields,
  type ComponentRenderer,
} from "./component-renderers.mjs";
export type AppTemplateConfig = {
  id: string;
  code: string;
  rendererVersion: number;
  theme: { accent: string; surface: string };
  pages: TemplatePageConfig[];
  components: AppComponent[];
  componentRenderers: ComponentRenderer[];
  bottomMenu: BottomMenu | null;
};
export function normalizeTemplate(value: unknown): AppTemplateConfig {
  const raw = value as Partial<AppTemplateConfig> | null;
  const pages = normalizeTemplatePages(
    raw?.pages,
    Number(raw?.rendererVersion) >= 2,
  );
  const result = {
    id: raw?.id || "TPL-DEFAULT",
    code: raw?.code || "default",
    rendererVersion: 2,
    theme: normalizeTemplateTheme(raw?.theme),
    pages,
    components: normalizeComponents(raw?.components),
    componentRenderers: normalizeRenderers(raw?.componentRenderers),
    bottomMenu:
      raw?.bottomMenu === null
        ? null
        : raw?.bottomMenu
          ? normalizeBottomMenus([raw.bottomMenu], [])[0]
          : normalizeBottomMenus(undefined, [
              {
                id: "TPL-DEFAULT",
                name: "默认模板",
                bottomMenuId: "BM-DEFAULT",
                pages,
              },
            ])[0],
  };
  if (!Array.isArray(raw?.components)) {
    const shared = consolidateBuiltinComponents({
      templates: [{ id: result.id, name: "默认模板", bottomMenuId: "", pages }],
      components: result.components,
      bottomMenus: [],
    }).configuration;
    return {
      ...result,
      pages: shared.templates[0].pages,
      components: shared.components,
    };
  }
  return result;
}
export const defaultTemplate = normalizeTemplate(null);
export function useTemplateConfig() {
  const [preview] = useState(() => {
    try {
      const hash = location.hash.slice(1);
      const data = new URLSearchParams(
        hash.startsWith("layoutPreview=") ? hash : hash.split("?")[1],
      ).get("layoutPreview");
      return data && data.length < 200000 ? JSON.parse(data) : null;
    } catch {
      return null;
    }
  });
  const [template, setTemplate] = useState<AppTemplateConfig>(() =>
    normalizeTemplate(preview),
  );
  const [navigation, setNavigation] = useState<CustomNavigation | null>(null);
  const [error, setError] = useState("");
  const [ready, setReady] = useState(IS_DEMO);
  useEffect(() => {
    if (IS_DEMO) return;
    const controller = new AbortController();
    getAppTemplate(controller.signal)
      .then((result) => {
        if (controller.signal.aborted) return;
        if (!result.template) throw new Error("模板不存在或已停用");
        const config = normalizeTemplate({
          ...(result.template as object),
          components: result.components,
          componentRenderers: result.componentRenderers,
          bottomMenu: result.bottomMenu,
        });
        const activeTemplate = preview
          ? { ...config, ...normalizeTemplate({ ...config, ...preview }) }
          : config;
        setTemplate(activeTemplate);
        const topGroupId =
          homeTopNavigation(activeTemplate)?.params.navigationId;
        const groups = (Array.isArray(result.items) ? result.items : []).filter(
          (group) => group.id !== topGroupId,
        );
        setNavigation({
          id: config.id,
          name: groups.length === 1 ? groups[0].name : "模板导航",
          displayTitle:
            groups.length === 1 && typeof groups[0].displayTitle === "string"
              ? groups[0].displayTitle.trim()
              : "",
          items: groups.flatMap((group) =>
            Array.isArray(group.items)
              ? group.items.map((item) => ({
                  ...item,
                  id: `${group.id}:${item.id}`,
                  categories: Array.isArray(item.categories)
                    ? item.categories
                    : [],
                  keywords: Array.isArray(item.keywords) ? item.keywords : [],
                  icon: typeof item.icon === "string" ? item.icon : "",
                }))
              : [],
          ),
        });
      })
      .catch((e) => {
        if (!controller.signal.aborted)
          setError(e instanceof Error ? e.message : "模板读取失败");
      })
      .finally(() => {
        if (!controller.signal.aborted) setReady(true);
      });
    return () => controller.abort();
  }, [preview]);
  return { template, navigation, error, ready, preview: !!preview };
}
const TemplateContext = createContext(defaultTemplate);
export const TemplateProvider = TemplateContext.Provider;
const RuntimeContext = createContext<{
  goToUrl: (url: string) => void;
  videoGrid: () => ReactNode;
  openVideo: (video: VideoItem) => void;
  openNavigation: (item: NavigationItem) => void;
  selectedNavigationId: string;
  autoSelectNavigation?: boolean;
}>({
  goToUrl: () => {},
  videoGrid: () => null,
  openVideo: () => {},
  openNavigation: () => {},
  selectedNavigationId: "",
});
export const TemplateRuntimeProvider = RuntimeContext.Provider;
export function homeTopNavigation(template: AppTemplateConfig) {
  const page = template.pages.find((p) => p.id === "home");
  return template.components.find(
    (c) =>
      c.type === "navigation" &&
      c.status === "启用" &&
      c.params.navigationPosition === "home-top" &&
      page?.blocks.some((b) => b.enabled && b.componentId === c.id),
  );
}
export function FeedTopNavigation() {
  const template = useContext(TemplateContext),
    runtime = useContext(RuntimeContext);
  const component = homeTopNavigation(template),
    id = component?.params.navigationId || "";
  const [group, setGroup] = useState<CustomNavigation | null>(null);
  const [error, setError] = useState("");
  const [retry, setRetry] = useState(0);
  const initialized = useRef("");
  useEffect(() => {
    const controller = new AbortController();
    setGroup(null);
    setError("");
    if (!id) return () => controller.abort();
    getNavigationGroup(id, controller.signal)
      .then((navigation) => {
        if (!controller.signal.aborted)
          setGroup({
            ...navigation,
            items: navigation.items.map((item) => ({
              ...item,
              id: `${navigation.id}:${item.id}`,
            })),
          });
      })
      .catch((e) => {
        if (!controller.signal.aborted) setError(e.message || "导航读取失败");
      });
    return () => controller.abort();
  }, [id, retry]);
  useEffect(() => {
    if (
      !group ||
      runtime.autoSelectNavigation === false ||
      initialized.current === group.id
    )
      return;
    initialized.current = group.id;
    const first = group.items.find(
      (item) => item.targetType !== "external_url",
    );
    if (first && !runtime.selectedNavigationId) runtime.openNavigation(first);
  }, [group, runtime]);
  const style = normalizeNavigationTabStyle(group?.tabStyle),
    renderer = template.componentRenderers.find(
      (r) => r.id === component?.rendererId,
    );
  return (
    <nav
      className="feed-tabs custom-feed-tabs"
      aria-label="首页顶部自定义导航"
      data-navigation-id={id}
      data-variant={style.variant}
      data-renderer-scope={component ? rendererScope(component.id) : undefined}
      style={
        {
          "--navigation-color": style.color,
          "--navigation-active-color": style.activeColor,
          "--navigation-indicator-color":
            style.indicatorColor || "var(--accent)",
          "--navigation-font-size": `${style.fontSize}px`,
          "--navigation-gap": `${style.gap}px`,
        } as CSSProperties
      }
    >
      {renderer?.css && component && (
        <style>
          {scopedRendererCss(renderer.css, rendererScope(component.id))}
        </style>
      )}
      {error ? (
        <button onClick={() => setRetry(retry + 1)}>导航加载失败，重试</button>
      ) : !group ? (
        <span role="status">加载导航…</span>
      ) : !group.items.length ? (
        <span>暂无启用菜单</span>
      ) : (
        group.items.map((item) => (
          <button
            key={item.id}
            className={`card title ${runtime.selectedNavigationId === item.id ? "selected" : ""}`}
            aria-pressed={runtime.selectedNavigationId === item.id}
            onClick={() => runtime.openNavigation(item)}
          >
            {item.name}
          </button>
        ))
      )}
    </nav>
  );
}

function DynamicComponent({ component }: { component: AppComponent }) {
  const runtime = useContext(RuntimeContext);
  const template = useContext(TemplateContext);
  const renderer = template.componentRenderers.find(
    (r) => r.id === component.rendererId,
  );
  const p = component.params;
  const [navigation, setNavigation] = useState<CustomNavigation | null>(null);
  const [videos, setVideos] = useState<RemoteVideo[]>([]);
  const [ads, setAds] = useState<ResolvedAds | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [retry, setRetry] = useState(0);
  const root = useRef<HTMLDivElement>(null);
  const seen = useRef(new Set<string>());
  const query = JSON.stringify(p);
  useEffect(() => {
    const controller = new AbortController();
    setLoading(true);
    setError("");
    setVideos([]);
    setAds(null);
    setNavigation(null);
    seen.current.clear();
    const request =
      component.type === "navigation"
        ? getNavigationGroup(p.navigationId, controller.signal).then(
            setNavigation,
          )
        : component.type === "ad-slot"
          ? getComponentAds(p.slotId, controller.signal).then(setAds)
          : getComponentVideos(p, controller.signal).then(setVideos);
    request
      .catch((e) => {
        if (!controller.signal.aborted) setError(e.message || "内容读取失败");
      })
      .finally(() => {
        if (!controller.signal.aborted) setLoading(false);
      });
    return () => controller.abort();
  }, [component.type, query, retry]);
  const adItems = ads?.items?.slice(0, p.limit) || [];
  useEffect(() => {
    if (
      !ads ||
      !root.current ||
      !adItems.length ||
      document.querySelector('[data-template-preview="true"]')
    )
      return;
    const observer = new IntersectionObserver(
      (entries) => {
        for (const entry of entries)
          if (entry.isIntersecting) {
            const item = adItems.find(
              (i) => i.plan.id === (entry.target as HTMLElement).dataset.plan,
            );
            if (item && !seen.current.has(item.plan.id)) {
              seen.current.add(item.plan.id);
              void reportComponentAd(ads, item, "impression");
            }
          }
      },
      { threshold: 0.5 },
    );
    root.current
      .querySelectorAll("[data-plan]")
      .forEach((el) => observer.observe(el));
    return () => observer.disconnect();
  }, [ads, query]);
  // Unfilled ad slots never reserve space; content blocks provide scoped feedback.
  if (component.type === "ad-slot" && (loading || error || !adItems.length))
    return null;
  const style = {
    padding: p.padding,
    borderRadius: p.radius,
    color: p.color || undefined,
    background: p.background || undefined,
    "--component-gap": `${p.gap}px`,
    "--component-columns": p.columns,
    "--component-image-size": `${p.imageSize}px`,
    "--component-font-size": `${p.fontSize}px`,
    "--component-radius": `${p.radius}px`,
  } as CSSProperties;
  const card = (
    key: string,
    title: string,
    description: string,
    image: string,
    onClick: () => void,
    ad?: ResolvedAdItem,
    plays?: string | number,
  ) => (
    <button
      type="button"
      key={key}
      className={`component-data-card image-${p.imagePosition}`}
      data-plan={ad?.plan.id}
      onClick={(e) => {
        e.stopPropagation();
        onClick();
      }}
    >
      {p.imagePosition !== "none" && image && (
        <img
          className="component-image"
          src={mediaUrl(image)}
          alt=""
          loading="lazy"
          style={{
            aspectRatio:
              p.aspectRatio === "original"
                ? undefined
                : p.aspectRatio.replace(":", "/"),
            objectFit: p.imageFit as "cover" | "contain",
          }}
        />
      )}
      {((p.showTitle && !!title) ||
        (p.showDescription && !!description) ||
        (p.showPlays && plays !== undefined)) && (
        <span>
          {p.showTitle && (
            <b
              className="component-title"
              style={{ color: p.titleColor || undefined }}
            >
              {title}
            </b>
          )}
          {p.showDescription && description && (
            <small className="component-description">{description}</small>
          )}
          {p.showPlays && plays !== undefined && (
            <small className="component-plays">播放 {plays}</small>
          )}
        </span>
      )}
    </button>
  );
  return (
    <div
      ref={root}
      className={`configured-component component-${component.type} component-data-block ${component.type === "ad-slot" && p.aspectRatio === "1:1" ? "component-icon-ads" : ""}`}
      style={style}
      data-component={component.id}
      data-renderer-scope={rendererScope(component.id)}
    >
      {renderer && (
        <style>
          {scopedRendererCss(renderer.css, rendererScope(component.id))}
        </style>
      )}
      {p.title && (
        <h2
          className="component-title"
          style={p.fontSize !== 14 ? { fontSize: p.fontSize } : undefined}
        >
          {p.url ? (
            <button
              type="button"
              onClick={(e) => {
                e.stopPropagation();
                runtime.goToUrl(p.url);
              }}
            >
              {p.title}
            </button>
          ) : (
            p.title
          )}
        </h2>
      )}
      {p.text && (
        <p
          className="component-description"
          style={p.fontSize !== 14 ? { fontSize: p.fontSize } : undefined}
        >
          {p.text}
        </p>
      )}
      {loading ? (
        <p role="status">正在读取内容…</p>
      ) : error ? (
        <button type="button" onClick={() => setRetry(retry + 1)}>
          读取失败，点击重试
        </button>
      ) : !videos.length && !adItems.length && !navigation?.items.length ? (
        <p>暂无符合条件的内容</p>
      ) : (
        <div className={`component-data-items layout-${p.arrangement}`}>
          {component.type === "navigation"
            ? navigation?.items
                .slice(0, p.limit)
                .map((item) =>
                  card(
                    item.id,
                    item.name,
                    "",
                    /^(https?:\/\/|\/(?!\/))/.test(item.icon) ? item.icon : "",
                    () => runtime.openNavigation(item),
                  ),
                )
            : component.type === "ad-slot"
              ? adItems.map((item) =>
                  card(
                    item.plan.id,
                    item.presentation?.title?.text || item.ad.name,
                    item.presentation?.description?.text ||
                      item.ad.description ||
                      item.ad.text ||
                      "",
                    item.ad.materials?.[0]?.url || "",
                    () => {
                      if (!item.destination) return;
                      if (
                        !document.querySelector(
                          '[data-template-preview="true"]',
                        )
                      )
                        void reportComponentAd(ads!, item, "click");
                      runtime.goToUrl(item.destination);
                    },
                    item,
                  ),
                )
              : videos
                  .slice(0, p.limit)
                  .map((v) =>
                    card(
                      v.id,
                      boundVideoFields(v, renderer?.fields).title,
                      boundVideoFields(v, renderer?.fields).description,
                      boundVideoFields(v, renderer?.fields).image,
                      () => runtime.openVideo(adaptVideo(v)),
                      undefined,
                      boundVideoFields(v, renderer?.fields).plays,
                    ),
                  )}
        </div>
      )}
    </div>
  );
}
function ComponentContent({ component }: { component: AppComponent }) {
  const runtime = useContext(RuntimeContext),
    template = useContext(TemplateContext),
    renderer = template.componentRenderers.find(
      (r) => r.id === component.rendererId,
    ),
    p = component.params;
  const style = {
    padding: p.padding,
    borderRadius: p.radius,
    color: p.color || undefined,
    background: p.background || undefined,
    "--component-gap": `${p.gap}px`,
    "--component-columns": p.columns,
  } as CSSProperties;
  const copy = (
    <>
      {p.title && (
        <h2
          className="component-title"
          style={p.fontSize !== 14 ? { fontSize: p.fontSize } : undefined}
        >
          {p.title}
        </h2>
      )}
      {p.text && (
        <p
          className="component-description"
          style={p.fontSize !== 14 ? { fontSize: p.fontSize } : undefined}
        >
          {p.text}
        </p>
      )}
    </>
  );
  const image = p.imageUrl ? (
    <img
      className="component-image"
      src={p.imageUrl}
      alt={p.title || component.name}
    />
  ) : null;
  let content: ReactNode = copy;
  if (component.type === "banner")
    content = (
      <>
        {image}
        <div className="component-banner-copy">{copy}</div>
      </>
    );
  if (component.type === "image")
    content = (
      <>
        {image}
        {copy}
      </>
    );
  if (component.type === "video-grid")
    content = (
      <>
        {p.url ? (
          <button type="button" onClick={() => runtime.goToUrl(p.url)}>
            {copy}
          </button>
        ) : (
          copy
        )}
        {runtime.videoGrid()}
      </>
    );
  if (
    ["content-list", "video-grid", "ad-slot", "navigation"].includes(
      component.type,
    )
  )
    return <DynamicComponent component={component} />;
  return (
    <div
      className={`configured-component component-${component.type}`}
      data-component={component.id}
      data-renderer-scope={rendererScope(component.id)}
      style={style}
    >
      {renderer && (
        <style>
          {scopedRendererCss(renderer.css, rendererScope(component.id))}
        </style>
      )}
      {p.url && component.type !== "video-grid" ? (
        <button type="button" onClick={() => runtime.goToUrl(p.url)}>
          {content}
        </button>
      ) : (
        content
      )}
    </div>
  );
}
function styledSlot(slot: ReactNode, component: AppComponent) {
  const p = component.params;
  if (
    !p.padding &&
    !p.gap &&
    !p.radius &&
    !p.color &&
    !p.background &&
    p.fontSize === 14
  )
    return slot;
  const style = {
    padding: p.padding || undefined,
    gap: p.gap || undefined,
    fontSize: p.fontSize !== 14 ? p.fontSize : undefined,
    borderRadius: p.radius || undefined,
    color: p.color || undefined,
    background: p.background || undefined,
  };
  if (
    isValidElement<{ children?: ReactNode }>(slot) &&
    isValidElement<{ style?: CSSProperties }>(slot.props.children)
  ) {
    const child = slot.props.children;
    return cloneElement(slot, {
      children: cloneElement(child, {
        style: { ...child.props.style, ...style },
      }),
    });
  }
  return <div style={style}>{slot}</div>;
}
export function TemplateBlock({
  children,
}: {
  id: string;
  children: ReactNode;
}) {
  return <>{children}</>;
}
type LayoutProps = {
  page: TemplatePageConfig;
  className: string;
  children: ReactNode;
};
function StackLayout({ page, className, children }: LayoutProps) {
  const runtime = useContext(RuntimeContext);
  return (
    <section
      className={`${className} template-page template-${page.layout}`}
      data-template-page={page.id}
      data-template-layout={page.layout}
      data-hidden-blocks={
        page.blocks
          .filter((block) => !block.enabled)
          .map((block) => block.id)
          .join(" ") || undefined
      }
      aria-label={page.title}
      style={
        {
          "--template-columns": page.columns,
          "--template-gap": `${page.gap}px`,
        } as CSSProperties
      }
    >
      {page.kind === "custom" && (
        <header className="simple-header">
          <button
            type="button"
            aria-label="返回首页"
            onClick={() => runtime.goToUrl("#home")}
          >
            ‹
          </button>
          <h1>{page.title}</h1>
        </header>
      )}
      {children}
    </section>
  );
}
// Layout registry holds code-owned renderers. Configuration only chooses a
// known renderer and known blocks, never executes supplied HTML or scripts.
const renderers: Record<string, typeof StackLayout> = Object.fromEntries(
  schema.pages.flatMap((page) =>
    page.layouts.map((layout) => [layout, StackLayout]),
  ),
);
export function TemplatePage({
  id,
  className,
  children,
  forceFeed = false,
}: {
  id: string;
  className: string;
  children: ReactNode;
  forceFeed?: boolean;
}) {
  const template = useContext(TemplateContext);
  const configured =
    template.pages.find((p) => p.id === id) ||
    defaultTemplate.pages.find((p) => p.id === id)!;
  const page =
    forceFeed && id === "home"
      ? { ...configured, layout: "video-feed" }
      : configured;
  const slots = new Map<string, ReactNode>();
  Children.forEach(children, (child) => {
    if (isValidElement<{ id?: string }>(child) && child.props.id)
      slots.set(child.props.id, child);
  });
  const Renderer = renderers[page.layout] || StackLayout;
  const renderBlock = (block: TemplatePageConfig["blocks"][number]) => {
    const component = template.components.find(
      (c) => c.id === block.componentId && c.status === "启用",
    );
    if (!component) return null;
    if (
      id === "home" &&
      component.type === "navigation" &&
      component.params.navigationPosition === "home-top"
    )
      return null;
    if (
      component.type === "builtin" &&
      gridSlots.includes(component.renderer) &&
      component.params.dataConfigured
    )
      return (
        <DynamicComponent component={{ ...component, type: "video-grid" }} />
      );
    if (component.type === "builtin") {
      const r = template.componentRenderers.find(
        (r) => r.id === component.rendererId,
      );
      const slot = styledSlot(slots.get(block.id), component);
      return r?.css ? (
        <div data-renderer-scope={rendererScope(component.id)}>
          <style>{scopedRendererCss(r.css, rendererScope(component.id))}</style>
          {slot}
        </div>
      ) : (
        slot
      );
    }
    return <ComponentContent component={component} />;
  };
  const isCustom = (block: TemplatePageConfig["blocks"][number]) =>
    template.components.find((c) => c.id === block.componentId)?.type !==
    "builtin";
  return (
    <Renderer page={page} className={className}>
      {page.blocks
        .filter(
          (block) =>
            block.enabled &&
            (id !== "home" ||
              (page.layout === "video-grid"
                ? ["header", "sorting", "catalog"].includes(block.id) ||
                  isCustom(block)
                : block.id !== "catalog")),
        )
        .map((block) => (
          <Fragment key={block.id}>
            {id === "home" && page.layout === "video-feed" && isCustom(block)
              ? null
              : renderBlock(block)}
          </Fragment>
        ))}
      {id === "home" && page.layout === "video-feed" && (
        <div className="feed-component-overlay">
          {page.blocks
            .filter((b) => b.enabled && isCustom(b))
            .map((b) => (
              <Fragment key={b.id}>{renderBlock(b)}</Fragment>
            ))}
        </div>
      )}
    </Renderer>
  );
}
export function TemplateFeedBlocks({ children }: { children: ReactNode }) {
  const template = useContext(TemplateContext);
  const page = template.pages.find((p) => p.id === "home")!;
  const slots = new Map<string, ReactNode>();
  Children.forEach(children, (child) => {
    if (isValidElement<{ id?: string }>(child) && child.props.id)
      slots.set(child.props.id, child);
  });
  return (
    <>
      {page.blocks
        .filter(
          (block) =>
            block.enabled &&
            template.components.find((c) => c.id === block.componentId)
              ?.type === "builtin",
        )
        .map((block) => (
          <Fragment key={block.id}>
            {styledSlot(
              slots.get(block.id),
              template.components.find((c) => c.id === block.componentId)!,
            )}
          </Fragment>
        ))}
    </>
  );
}
